import React, { useState } from "react";
import { Modal, Collapse, Button, Typography } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import dayjs from "dayjs";

const { Panel } = Collapse;
const { Text } = Typography;

type ConfirmModalProps = {
  setIsModalOpen: CallableFunction;
  isModalOpen: boolean;
  handleOk: () => void;
  handleCancel: () => void;
  handleSubmitReferral?: CallableFunction;
  isReferralLoading: boolean;
  message: string;
  patientFormData?: any;
  flagForPatientReg: boolean;
  cardReferralError?: any;
};

const ConfirmModal: React.FC<ConfirmModalProps> = (props) => {
  const { patientFormData } = props;
  const [showAllServices, setShowAllServices] = useState(false);

  const toggleServices = () => setShowAllServices((prev) => !prev);

  // Professional field renderer with clear label-value separation
  const renderField = (label: string, value: any, className = "") => (
    <div className={`field-row ${className}`}>
      <div className="field-label">
        <Text className="label-text">{label}</Text>
      </div>
      <div className="field-value">
        <Text className="value-text">{value || "N/A"}</Text>
      </div>
    </div>
  );

  // Dynamic data renderer - Professional mobile layout
  const renderDynamicData = (data: any) => {
    if (!data) return <Text className="no-data">No data available</Text>;

    return (
      <div className="dynamic-section">
        {Object.keys(data)
          ?.filter(
            (key) =>
              key.toLowerCase() !== "document" &&
              key.toLowerCase() !== "id" &&
              key.toLowerCase() !== "schemeid"
          )
          ?.map((key) => {
            if (key.toLowerCase() === "services") {
              const services = Array.isArray(data[key]) ? data[key] : [];
              return (
                <div className="services-section" key={key}>
                  <div className="section-header">
                    <Text strong className="section-title">SERVICES ({services.length})</Text>
                  </div>
                  <div className="services-container">
                    {services.slice(0, showAllServices ? services.length : 1).map((service: any, index: number) => (
                      <div key={index} className="service-card">
                        {Object.entries(service)
                          ?.filter(([serviceKey]) => serviceKey.toLowerCase() !== "id")
                          ?.map(([serviceKey, serviceValue]) => (
                            <div key={serviceKey} className="field-row service-field">
                              <div className="field-label">
                                <span className="label-text">
                                  {serviceKey.toLowerCase() === "name" 
                                    ? "Component" 
                                    : serviceKey.replace(/([A-Z])/g, " $1").toUpperCase()}
                                </span>
                              </div>
                              <div className="field-value">
                                <span className="value-text">{serviceValue ? String(serviceValue) : "-"}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    ))}
                    {services.length > 1 && (
                      <Button
                        type="link"
                        onClick={toggleServices}
                        className="toggle-services-btn"
                        size="small"
                      >
                        {showAllServices ? "Show Less (-)" : `+${services.length - 1} More`}
                      </Button>
                    )}
                  </div>
                </div>
              );
            }

            if (key.toLowerCase() === "ipallowed") {
              return renderField(
                key.replace(/([A-Z])/g, " $1").toUpperCase(),
                data[key] === true ? "Yes" : "No",
                "boolean-field"
              );
            }

            const value = Array.isArray(data[key])
              ? data[key].map((item: any, idx: number) => (
                  <div key={idx} className="nested-item">
                    {typeof item === "object" ? renderDynamicData(item) : item}
                  </div>
                ))
              : typeof data[key] === "object" && data[key] !== null
              ? renderDynamicData(data[key])
              : typeof data[key] === "boolean"
              ? data[key] ? "Yes" : "No"
              : data[key];

            return renderField(
              key.replace(/([A-Z])/g, " $1").toUpperCase(),
              value
            );
          })}
      </div>
    );
  };

  return (
    <>
      <style jsx>{`
        .professional-confirm-modal :global(.ant-modal) {
          max-width: 95vw !important;
          width: 380px !important;
          margin: 0 auto !important;
        }
        
        .professional-confirm-modal :global(.ant-modal-body) {
          padding: 20px !important;
          max-height: 75vh !important;
          overflow-y: auto !important;
          padding-bottom: 80px !important;
        }
        
        .professional-confirm-modal :global(.ant-modal-title) {
          display: flex !important;
          align-items: center !important;
          font-size: 12px !important;
          font-weight: 600 !important;
        }
        
        .field-row {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px 0;
          border-bottom: 1px solid #f0f2f5;
          min-height: 44px;
        }
        
        .field-label {
          flex-shrink: 0;
          min-width: 130px;
          padding-right: 8px;
        }
        
        .label-text {
          font-weight: 600 !important;
          color: #1f2937 !important;
          font-size: 12px !important;
          line-height: 1 !important;
          display: block;
        }
        
        .field-value {
          flex: 1;
          min-width: 0;
        }
        
        .value-text {
          color: #374151 !important;
          font-size: 12px !important;
          line-height: 1 !important;
          word-break: break-word !important;
          display: block;
        }
        
        .info-grid .field-row {
          padding: 10px 0;
        }
        
        .services-section {
          background: #f8fafc;
          border-radius: 8px;
          padding: 8px;
          border: 1px solid #e5e7eb;
          margin: 6px 0;
        }
        
        .section-header {
          margin-bottom: 10px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .section-title {
          font-size: 12px !important;
          color: #1e40af !important;
        }
        
        .services-container {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .service-card {
          background: #ffffff;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          padding: 10px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        
        .service-field {
          padding: 8px 0 !important;
          border-bottom: none !important;
          min-height: auto !important;
        }
        
        .service-field:last-child {
          margin-bottom: 0 !important;
        }
        
        .consent-section {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 2px solid #e5e7eb;
        }
        
        .confirm-message {
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 2px solid #f0f2f5;
        }
        
        .message-text {
          font-size: 12px !important;
          font-weight: 500 !important;
          color: #111827 !important;
          line-height: 1 !important;
        }
        
        .error-banner {
          margin-top: 10px;
          padding: 10px;
          background: linear-gradient(135deg, #fef3f2 0%, #fce7e6 100%);
          border: 1px solid #fca5a5;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .error-text {
          font-size: 12px !important;
          color: #dc2626 !important;
          font-weight: 500 !important;
          line-height: 1 !important;
        }
        
        .professional-collapse :global(.ant-collapse-header) {
          font-weight: 600 !important;
          padding: 14px 12px !important;
          font-size: 12px !important;
          border-bottom: 2px solid #f0f2f5 !important;
        }
        
        .professional-collapse :global(.ant-collapse-content-box) {
          padding: 16px !important;
          background: #fafbfc !important;
        }
        
        .no-data {
          color: #6b7280 !important;
          font-style: italic !important;
          text-align: center !important;
          padding: 12px 6px !important;
          font-size: 12px !important;
        }
        
        .toggle-services-btn {
          padding: 4px 0 !important;
          height: auto !important;
          font-size: 12px !important;
          color: #3b82f6 !important;
          margin-top: 8px !important;
        }
        
        .toggle-services-btn:hover {
          background: none !important;
          color: #1d4ed8 !important;
        }
        
        .nested-item {
          margin: 8px 0;
        }
        
        /* Mobile optimizations */
        @media (max-width: 480px) {
          .professional-confirm-modal :global(.ant-modal) {
            width: 95vw !important;
            margin: 8px !important;
          }
          
          .field-label {
            min-width: 110px !important;
          }
          
          .field-row {
            flex-direction: column !important;
            gap: 4px !important;
            padding: 12px 0 !important;
          }
          
          .field-label {
            min-width: auto !important;
            font-size: 12px !important;
          }
        }
        
        /* Perfect scroll behavior */
        .professional-confirm-modal :global(.ant-modal-body::-webkit-scrollbar) {
          width: 4px;
        }
        
        .professional-confirm-modal :global(.ant-modal-body::-webkit-scrollbar-track) {
          background: #f1f5f9;
          border-radius: 2px;
        }
        
        .professional-confirm-modal :global(.ant-modal-body::-webkit-scrollbar-thumb) {
          background: #cbd5e1;
          border-radius: 2px;
        }
      `}</style>

      <Modal
        maskClosable={false}
        title={
          <div style={{ display: 'flex', alignItems: 'center', fontSize: '16px', fontWeight: 600 }}>
            <ExclamationCircleFilled style={{ color: "#faad14", marginRight: 8, fontSize: '18px' }} />
            Confirm Details
          </div>
        }
        open={props?.isModalOpen}
        onOk={props?.handleOk}
        onCancel={props?.handleCancel}
        confirmLoading={props.isReferralLoading}
        okText="YES"
        cancelText="NO"
        centered
        width={380}
        className="professional-confirm-modal"
        styles={{
          body: { padding: '20px' }
        }}
      >
        {/* Confirmation Message */}
        <div className="confirm-message">
          <Text className="message-text">{props?.message}?</Text>
          {props?.cardReferralError && (
            <div className="error-banner">
              <Text className="error-text">
                ⚠️ {props.cardReferralError}. Do you still want to proceed?
              </Text>
            </div>
          )}
        </div>

        {/* Patient Registration Details */}
        {props?.flagForPatientReg && (
          <Collapse 
            defaultActiveKey={["2"]}
            ghost
            className="professional-collapse"
          >
            <Panel 
              header={<Text strong style={{ fontSize: '15px', color: '#1f2937' }}>👤 Patient Information</Text>} 
              key="1"
            >
              <div className="info-grid">
                {renderField("Name", patientFormData?.patientRequest?.fullName)}
                {renderField("UHID", patientFormData?.patientRequest?.uhidNo)}
                {renderField(
                  (() => {
                    const id = patientFormData?.patientRequest?.patientSchemeDetails?.schemeMetaDataId;
                    switch (id) {
                      case 1:
                      case 10: return "Beneficiary ID";
                      case 2: return "Beneficiary ID";
                      case 3:
                      case 4: return "Medical Card ID";
                      default: return "Identity Number";
                    }
                  })(),
                  patientFormData?.patientRequest?.identityNumber
                )}
                {renderField("Age", patientFormData?.patientRequest?.age)}
                {renderField("Gender", patientFormData?.patientRequest?.sex)}
                {renderField("Email", patientFormData?.patientRequest?.email)}
                {renderField("Primary Mobile", patientFormData?.patientRequest?.primaryMobile)}
                {/* {renderField("Secondary Mobile", patientFormData?.patientRequest?.secondaryMobile)} */}
                {renderField("Scheme", patientFormData?.patientRequest?.patientSchemeDetails?.schemeName)}
                
                {patientFormData?.patientRequest?.communicationConsent && (
                  <div className="consent-section">
                    {renderField("Communication Consent", patientFormData?.patientRequest?.communicationConsent ? "YES" : "NO")}
                    {renderField("Primary Policy Holder", patientFormData?.patientRequest?.isPrimaryPolicyHolder ? "YES" : "NO")}
                    {(patientFormData?.patientRequest?.patientSchemeDetails?.schemeName?.toLowerCase()?.includes("cghs") ||
                      patientFormData?.patientRequest?.patientSchemeDetails?.schemeName?.toLowerCase()?.includes("echs")) &&
                      renderField("Card Category", patientFormData?.patientRequest?.cardCategory)
                    }
                  </div>
                )}
              </div>
            </Panel>

            <Panel 
              header={<Text strong style={{ fontSize: '15px', color: '#1f2937' }}>📋 Visit Information</Text>} 
              key="2"
            >
              <div className="info-grid">
                {renderField("Visit Type", patientFormData?.visitInfoRequest?.visitType)}
                {renderField(
                  (patientFormData?.visitInfoRequest?.visitType === "IP" || 
                   patientFormData?.visitInfoRequest?.visitType === "EMERGENCY") 
                    ? "Date of Admission" 
                    : "Date of Visit",
                  patientFormData?.visitInfoRequest?.doa && 
                  dayjs(patientFormData.visitInfoRequest.doa).format("DD-MM-YYYY")
                )}
                {/* {renderField("Department", patientFormData?.visitInfoRequest?.department?.join(", "))} */}
                {/* {renderField(
                  (patientFormData?.visitInfoRequest?.visitType === "IP" || 
                   patientFormData?.visitInfoRequest?.visitType?.toLowerCase() === "emergency") 
                    ? "IP Number" 
                    : "OP Number",
                  patientFormData?.visitInfoRequest?.opNumber
                )} */}
                {patientFormData?.visitInfoRequest?.totalBillAmount && 
                  renderField("Expected Bill Amount", patientFormData?.visitInfoRequest?.totalBillAmount)}
                {patientFormData?.visitInfoRequest?.roomNumber && 
                  renderField("Room Number", patientFormData?.visitInfoRequest?.roomNumber)}
                {/* {renderField("Doctor", patientFormData?.visitInfoRequest?.doctorDetails?.split(",")[1] || "")} */}
              </div>
            </Panel>

            {patientFormData?.patientRequest?.referralInfoRequest?.echsReferralRequest && (
              <Panel header={<Text strong style={{ fontSize: '15px', color: '#1f2937' }}>🏥 ECHS Referral</Text>} key="3">
                {renderDynamicData(patientFormData?.patientRequest?.referralInfoRequest?.echsReferralRequest)}
              </Panel>
            )}
            
            {patientFormData?.patientRequest?.referralInfoRequest?.cghsReferralRequest &&
              patientFormData?.patientRequest?.patientSchemeDetails?.schemeMetaDataId !== 10 &&
              patientFormData?.patientRequest?.patientSchemeDetails?.schemeName?.toUpperCase() !== "CAPF" && (
              <Panel header={<Text strong style={{ fontSize: '15px', color: '#1f2937' }}>🏥 CGHS Referral</Text>} key="4">
                {patientFormData?.patientRequest?.referralInfoRequest?.cghsReferralRequest 
                  ? renderDynamicData(patientFormData?.patientRequest?.referralInfoRequest?.cghsReferralRequest)
                  : <Text className="no-data">No data available</Text>}
              </Panel>
            )}
          </Collapse>
        )}
      </Modal>
    </>
  );
};

export default ConfirmModal;
